import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { widget } from 'charting_library';
import useDarkMode from 'use-dark-mode';
import pako from 'pako';

// Simple timeframe mapping
const TIME_FRAME_MAP = {
  '1': '1min',
  '5': '5min', 
  '15': '15min',
  '30': '30min',
  '60': '60min',
  '240': '4hour',
  '1D': '1day',
  '1W': '1week',
  '1M': '1mon'
};

// Get language from URL (cached)
const getLanguage = () => {
  const match = window.location.search.match(/[?&]lang=([^&#]*)/);
  return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : 'en';
};

export default function TVChartContainer({ symbol = 'ETH-USDT', socketKlines }) {
  const chartContainerRef = useRef();
  const widgetRef = useRef(null);
  const websocketRef = useRef(null);
  const subscriptionRef = useRef(null);
  const darkMode = useDarkMode(false);
  const isChartReadRef = useRef(false);
  // Memoized datafeed to prevent recreation
  const datafeed = useMemo(() => ({
    onReady: (callback) => {
      setTimeout(() => {
      callback({
        supported_resolutions: ['1', '5', '15', '30', '60', '240', '1D', '1W', '1M'],
        supports_time: true,
        supports_marks: false,
        supports_timescale_marks: false
      })
      isChartReadRef.current = true;
      }, 0)
    },

    searchSymbols: (userInput, exchange, symbolType, onResultReadyCallback) => {
      onResultReadyCallback([]);
    },

    resolveSymbol: (symbolName, onSymbolResolvedCallback) => {
      const symbolInfo = {
        name: symbolName,
        description: symbolName,
        type: 'crypto',
        session: '24x7',
        timezone: 'Etc/UTC',
        minmov: 1,
        pricescale: 100000,
        has_intraday: true,
        has_daily: true,
        has_weekly_and_monthly: true,
        supported_resolutions: ['1', '5', '15', '30', '60', '240', '1D', '1W', '1M'],
        volume_precision: 8,
        data_status: 'streaming'
      };
      setTimeout(() => onSymbolResolvedCallback(symbolInfo), 0);
    },

    getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
      try {
        const { from, to } = periodParams;
        const htxSymbol = symbolInfo.name.toLowerCase().replace('-', '');
        const period = TIME_FRAME_MAP[resolution] || '15min';
        
        const url = `https://api.htx.com/market/history/kline?symbol=${htxSymbol}&period=${period}&size=1000`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.status === 'ok' && Array.isArray(data.data)) {
          const bars = data.data
            .map(bar => ({
              time: bar.id * 1000,
              open: parseFloat(bar.open),
              high: parseFloat(bar.high),
              low: parseFloat(bar.low),
              close: parseFloat(bar.close),
              volume: parseFloat(bar.vol || 0)
            }))
            .filter(bar => bar.time >= from * 1000 && bar.time <= to * 1000)
            .sort((a, b) => a.time - b.time);

          onHistoryCallback(bars, { noData: bars.length === 0 });
        } else {
          onHistoryCallback([], { noData: true });
        }
      } catch (error) {
        console.error('Error fetching bars:', error);
        onErrorCallback(error);
      }
    },

    subscribeBars: (symbolInfo, resolution, onRealtimeCallback, subscriberUID) => {
      subscriptionRef.current = {
        symbol: symbolInfo.name,
        id: subscriberUID,
        handler: onRealtimeCallback
      };
      
      // Connect simple WebSocket
      connectWebSocket(symbolInfo.name, resolution);
    },

    unsubscribeBars: (subscriberUID) => {
      console.log("in unsubscribeBars function")
      if (subscriptionRef.current?.id === subscriberUID) {
        closeWebSocket();
        subscriptionRef.current = null;
      }
    }
  }), []);

  // Simplified WebSocket connection
  const connectWebSocket = useCallback((symbolName, resolution) => {
    closeWebSocket();
    
    const htxSymbol = symbolName.toLowerCase().replace('-', '');
    const period = TIME_FRAME_MAP[resolution] || '15min';
    
    websocketRef.current = new WebSocket('wss://api.htx.com/ws');
    
    websocketRef.current.onopen = () => {
      const subscribeMessage = {
        sub: `market.${htxSymbol}.kline.${period}`,
        id: Date.now().toString()
      };
      websocketRef.current.send(JSON.stringify(subscribeMessage));
    };

    websocketRef.current.onmessage = async (event) => {
      try {
        let data;
        if (event.data instanceof Blob) {
          // Handle binary compressed data from HTX
          const arrayBuffer = await event.data.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const decompressed = pako.inflate(uint8Array, { to: 'string' });
          data = JSON.parse(decompressed);
        } else {
          data = JSON.parse(event.data);
        }

        // Handle ping/pong
        if (data.ping) {
          websocketRef.current.send(JSON.stringify({ pong: data.ping }));
          return;
        }

        // Handle kline data
        if (data.tick && subscriptionRef.current) {
          const tick = data.tick;
          const bar = {
            time: tick.id * 1000,
            open: parseFloat(tick.open),
            high: parseFloat(tick.high),
            low: parseFloat(tick.low),
            close: parseFloat(tick.close),
            volume: parseFloat(tick.vol || 0)
          };
          subscriptionRef.current.handler(bar);
        }
      } catch (error) {
        console.error('WebSocket message error:', error);
      }
    };

    websocketRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  }, []);

  const closeWebSocket = useCallback(() => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
  }, []);

  useEffect(() => {
    console.log("socketKlines", socketKlines)
    if (socketKlines?.data?.length && subscriptionRef.current) {
      const latestBar = socketKlines?.data[socketKlines.klines.data.length - 1];
      if (latestBar?.close) {
        const bar = {
          time: (latestBar.time < 10000000000 ? latestBar.time * 1000 : latestBar.time),
          open: parseFloat(latestBar.open),
          high: parseFloat(latestBar.high),
          low: parseFloat(latestBar.low),
          close: parseFloat(latestBar.close),
          volume: parseFloat(latestBar.volume || 0)
        };
        // subscriptionRef.current.handler(bar);
      }
    }
  }, [socketKlines]);

  useEffect(() => {
    if (chartContainerRef.current) {
      widgetRef.current = new widget({
        symbol,
        datafeed,
        interval: '15',
        container: chartContainerRef.current,
        library_path: '/charting_library/',
        locale: getLanguage(),
        autosize: true,
        theme: darkMode.value ? 'Dark' : 'Light',
      });
    }
    return () => {

      widgetRef.current?.remove();
      widgetRef.current = null;
    };
  }, [chartContainerRef]);

  // Main widget initialization - only recreate when absolutely necessary
  useEffect(() => {
    if (widgetRef.current && isChartReadRef.current) {
        const active = widgetRef.current?.activeChart?.();
        if(active)
          widgetRef.current.setSymbol(symbol, "15", () => {
          });
  }
  
  }, [symbol, darkMode.value, datafeed]);

  return (
    <div
      ref={chartContainerRef}
      className="TVChartContainer"
      style={{ height: '100%', width: '100%' }}
    />
  );
}
